import { useEffect, useState, type FormEvent } from 'react';
import type {
  BillingClass,
  BillingClassBillType,
  CreateBillingClassInput,
} from '@catalyst-psa/shared';
import { BILLING_CLASS_BILL_TYPES } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { BILL_TYPE_LABELS } from './billType.js';
import {
  useCreateBillingClass,
  useDeleteBillingClass,
  useUpdateBillingClass,
} from './api.js';

interface BillingClassFormModalProps {
  isOpen: boolean;
  /** Existing billing class to edit, or null to create a new one. */
  billingClass: BillingClass | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  billType: BillingClassBillType;
  showOnInvoice: boolean;
  isDefault: boolean;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  billType: 'bill',
  showOnInvoice: true,
  isDefault: false,
  isActive: true,
};

const BILL_TYPE_OPTIONS = BILLING_CLASS_BILL_TYPES.map((value) => ({
  value,
  label: BILL_TYPE_LABELS[value],
}));

function toFormState(billingClass: BillingClass | null): FormState {
  if (!billingClass) {
    return EMPTY_FORM;
  }
  return {
    name: billingClass.name,
    billType: billingClass.billType,
    showOnInvoice: billingClass.showOnInvoice,
    isDefault: billingClass.isDefault,
    isActive: billingClass.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function BillingClassFormModal({
  isOpen,
  billingClass: billingClassProp,
  onClose,
}: BillingClassFormModalProps): JSX.Element {
  const [billingClass, setBillingClass] = useState<BillingClass | null>(billingClassProp);
  const isEdit = billingClass !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(billingClass));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateBillingClass();
  const updateMutation = useUpdateBillingClass();
  const deleteMutation = useDeleteBillingClass();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setBillingClass(billingClassProp);
    }
  }, [isOpen, billingClassProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(billingClass));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, billingClass, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save billing class.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateBillingClassInput = {
      name: form.name.trim(),
      billType: form.billType,
      showOnInvoice: form.showOnInvoice,
      isDefault: form.isDefault,
      isActive: form.isActive,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: billingClass.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setBillingClass(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!billingClass) {
      return;
    }
    deleteMutation.mutate(billingClass.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit billing class' : 'New billing class'}
        footer={
          <FormActions
            formId="billing-class-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="billing-class-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <TextField
            label="Name"
            required
            value={form.name}
            maxLength={200}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
          />

          <SelectField
            label="Billing behavior"
            required
            value={form.billType}
            options={BILL_TYPE_OPTIONS}
            onChange={(e) =>
              setForm((f) => ({ ...f, billType: e.target.value as BillingClassBillType }))
            }
            error={fieldErrors.billType}
          />

          <CheckboxField
            label="Show on invoice"
            checked={form.showOnInvoice}
            onChange={(e) => setForm((f) => ({ ...f, showOnInvoice: e.target.checked }))}
          />

          <CheckboxField
            label="Default for new time entries"
            checked={form.isDefault}
            onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
          />

          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete billing class"
        message={`Delete "${billingClass?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
